import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Cookies from "universal-cookie";
import moment from "moment-timezone";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import host from '../../assets/js/Host';
import Login from '../common/login';

const cookies = new Cookies();
const role = Number(localStorage.getItem('role'));

// ============================================================
// 🎨 Styles
// ============================================================

const styles = `
    :root {
        --primary: #3b82f6;
        --primary-dark: #2563eb;
        --success: #10b981;
        --success-light: #d1fae5;
        --danger: #ef4444;
        --danger-light: #fee2e2;
        --warning: #f59e0b;
        --warning-light: #fef3c7;
        --info: #06b6d4;
        --info-light: #cffafe;
        --gray-50: #f9fafb;
        --gray-100: #f3f4f6;
        --gray-200: #e5e7eb;
        --gray-300: #d1d5db;
        --gray-500: #6b7280;
        --gray-700: #374151;
        --gray-900: #111827;
        --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
        --shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
        --shadow-lg: 0 10px 15px -3px rgba(0,0,0,0.1);
        --shadow-xl: 0 20px 25px -5px rgba(0,0,0,0.1);
        --radius: 12px;
        --radius-lg: 16px;
    }

    * { box-sizing: border-box; }

    .dashboard-v4 {
        min-height: 100vh;
        background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%);
        padding: 24px;
        direction: rtl;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    .dashboard-container {
        max-width: 1600px;
        margin: 0 auto;
    }

    .dashboard-header-v4 {
        text-align: center;
        margin-bottom: 32px;
    }

    .dashboard-title-v4 {
        font-size: 2.8rem;
        font-weight: 900;
        color: var(--gray-900);
        margin-bottom: 10px;
    }

    .dashboard-subtitle-v4 {
        font-size: 1.3rem;
        color: var(--gray-600);
        font-weight: 600;
    }

    .filter-card-v4 {
        background: white;
        border-radius: var(--radius-lg);
        padding: 24px;
        margin-bottom: 24px;
        box-shadow: var(--shadow);
    }

    .filter-row-v4 {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: flex-end;
    }

    .filter-group-v4 {
        flex: 1;
        min-width: 200px;
    }

    .filter-label-v4 {
        display: block;
        font-weight: 600;
        color: var(--gray-700);
        margin-bottom: 8px;
        font-size: 0.9rem;
    }

    .filter-select-v4, .filter-input-v4 {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid var(--gray-200);
        border-radius: var(--radius);
        font-size: 1rem;
        transition: all 0.2s;
        background: white;
    }

    .filter-select-v4:focus, .filter-input-v4:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .btn-primary-v4 {
        background: linear-gradient(135deg, var(--primary), var(--primary-dark));
        color: white;
        border: none;
        padding: 12px 32px;
        border-radius: var(--radius);
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .btn-primary-v4:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-lg);
    }

    .btn-primary-v4:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
    }

    .summary-grid-v4 {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
    }

    .summary-card-v4 {
        background: white;
        border-radius: var(--radius-lg);
        padding: 20px;
        box-shadow: var(--shadow);
        position: relative;
        overflow: hidden;
        text-align: center;
    }

    .summary-card-v4::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 4px;
        height: 100%;
        background: var(--primary);
    }

    .summary-card-v4.success::before { background: var(--success); }
    .summary-card-v4.danger::before { background: var(--danger); }
    .summary-card-v4.warning::before { background: var(--warning); }
    .summary-card-v4.info::before { background: var(--info); }

    .summary-icon-v4 {
        font-size: 2.2rem;
        margin-bottom: 10px;
    }

    .summary-value-v4 {
        font-size: 2rem;
        font-weight: 900;
        color: var(--gray-900);
        margin-bottom: 6px;
    }

    .summary-label-v4 {
        font-size: 1rem;
        color: var(--gray-700);
        font-weight: 700;
    }

    /* Charts Grid */
    .charts-grid-v4 {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 20px;
        margin-bottom: 24px;
    }

    .simple-chart-v4 {
        background: white;
        border-radius: var(--radius-lg);
        padding: 20px;
        box-shadow: var(--shadow);
    }

    .chart-title-v4 {
        font-size: 1.2rem;
        font-weight: 800;
        color: var(--gray-900);
        margin-bottom: 16px;
        text-align: center;
    }

    .bar-chart-container {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        height: 200px;
        padding: 30px 10px 10px 10px;
        border-bottom: 2px solid var(--gray-200);
        gap: 8px;
        overflow-x: auto;
        background: var(--gray-50);
        border-radius: 8px;
    }

    .bar-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 40px;
        flex: 1;
        height: 100%;
        justify-content: flex-end;
    }

    .bar {
        width: 100%;
        max-width: 40px;
        min-height: 10px;
        background: linear-gradient(180deg, #10b981, #059669);
        border-radius: 6px 6px 0 0;
        transition: all 0.3s;
        position: relative;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .bar.negative {
        background: linear-gradient(180deg, #ef4444, #dc2626);
    }

    .bar.positive {
        background: linear-gradient(180deg, #10b981, #059669);
    }

    .bar.sales {
        background: linear-gradient(180deg, #f59e0b, #d97706);
    }

    .bar:hover {
        opacity: 0.85;
        transform: scaleX(1.1);
    }

    .bar-label {
        font-size: 0.7rem;
        color: var(--gray-600);
        margin-top: 8px;
        text-align: center;
        font-weight: 500;
    }

    .bar-value {
        font-size: 0.65rem;
        color: var(--gray-800);
        font-weight: 700;
        position: absolute;
        top: -20px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        background: white;
        padding: 2px 4px;
        border-radius: 4px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }

    .campaigns-header-v4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        flex-wrap: wrap;
        gap: 16px;
    }

    .campaigns-title-v4 {
        font-size: 1.6rem;
        font-weight: 900;
        color: var(--gray-900);
    }

    .campaigns-controls-v4 {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    .search-input-v4 {
        padding: 10px 16px;
        border: 2px solid var(--gray-200);
        border-radius: var(--radius);
        font-size: 0.95rem;
        min-width: 220px;
    }

    .search-input-v4:focus {
        outline: none;
        border-color: var(--primary);
    }

    .campaigns-grid-v4 {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 20px;
    }

    .campaign-card-v4 {
        background: white;
        border-radius: var(--radius-lg);
        overflow: hidden;
        box-shadow: var(--shadow);
        transition: all 0.3s;
        border: 2px solid transparent;
    }

    .campaign-card-v4:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-xl);
    }

    .campaign-card-v4.profitable {
        border-color: var(--success);
        background: linear-gradient(135deg, #f0fdf4 0%, white 100%);
    }

    .campaign-card-v4.losing {
        border-color: var(--danger);
        background: linear-gradient(135deg, #fef2f2 0%, white 100%);
    }

    .campaign-header-v4 {
        padding: 14px 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid var(--gray-100);
        flex-wrap: wrap;
        gap: 10px;
    }

    .campaign-card-v4.profitable .campaign-header-v4 {
        background: rgba(16, 185, 129, 0.1);
    }

    .campaign-card-v4.losing .campaign-header-v4 {
        background: rgba(239, 68, 68, 0.1);
    }

    .campaign-name-v4 {
        font-size: 1.1rem;
        font-weight: 800;
        color: var(--gray-900);
    }

    .campaign-product-v4 {
        font-size: 0.95rem;
        color: var(--gray-600);
        margin-top: 4px;
        font-weight: 600;
    }

    .campaign-badge-v4 {
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 0.95rem;
        font-weight: 800;
    }

    .campaign-badge-v4.profit {
        background: var(--success);
        color: white;
    }

    .campaign-badge-v4.loss {
        background: var(--danger);
        color: white;
    }

    .campaign-body-v4 {
        padding: 18px;
    }

    .campaign-stats-v4 {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin-bottom: 14px;
    }

    .stat-item-v4 {
        padding: 12px 8px;
        background: var(--gray-50);
        border-radius: var(--radius);
        text-align: center;
    }

    .stat-item-v4.highlight {
        background: var(--info-light);
        border: 1px solid var(--info);
    }

    .stat-value-v4 {
        font-size: 1.2rem;
        font-weight: 900;
        color: var(--gray-900);
    }

    .stat-label-v4 {
        font-size: 0.75rem;
        color: var(--gray-600);
        margin-top: 4px;
        font-weight: 700;
    }

    .campaign-alerts-v4 {
        margin-top: 10px;
    }

    .alert-item-v4 {
        padding: 10px 12px;
        border-radius: var(--radius);
        font-size: 0.95rem;
        font-weight: 700;
        margin-bottom: 8px;
    }

    .alert-item-v4.warning {
        background: var(--warning-light);
        color: #92400e;
    }

    .alert-item-v4.danger {
        background: var(--danger-light);
        color: #991b1b;
    }

    .alert-item-v4.success {
        background: var(--success-light);
        color: #065f46;
    }

    .campaign-table-v4 {
        width: 100%;
        border-collapse: collapse;
        margin-top: 14px;
        font-size: 0.9rem;
    }

    .campaign-table-v4 th {
        background: var(--gray-100);
        padding: 10px 6px;
        text-align: center;
        font-weight: 700;
        color: var(--gray-700);
        font-size: 0.95rem;
    }

    .campaign-table-v4 td {
        padding: 10px 6px;
        text-align: center;
        border-bottom: 1px solid var(--gray-100);
        font-weight: 700;
        font-size: 0.9rem;
        color: var(--gray-800);
    }

    .campaign-table-v4 tr.red-row {
        background: var(--danger-light);
    }

    .btn-row-v4 {
        display: flex;
        gap: 8px;
        margin-top: 14px;
    }

    .btn-details-v4 {
        flex: 1;
        padding: 12px;
        background: var(--gray-100);
        border: none;
        border-radius: var(--radius);
        font-size: 1rem;
        font-weight: 800;
        color: var(--gray-700);
        cursor: pointer;
        transition: all 0.2s;
    }

    .btn-details-v4:hover {
        background: var(--gray-200);
    }

    .btn-tiktok-v4 {
        background: linear-gradient(135deg, #00f2ea, #ff0050);
        color: white;
        border: none;
        padding: 12px 18px;
        border-radius: var(--radius);
        font-size: 1rem;
        font-weight: 800;
        cursor: pointer;
        transition: all 0.2s;
    }

    .btn-tiktok-v4:hover {
        transform: scale(1.02);
    }

    .loading-overlay-v4 {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .spinner-v4 {
        width: 50px;
        height: 50px;
        border: 4px solid white;
        border-top-color: var(--primary);
        border-radius: 50%;
        animation: spin-v4 1s linear infinite;
    }

    @keyframes spin-v4 {
        to { transform: rotate(360deg); }
    }

    .modal-overlay-v4 {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
    }

    .modal-content-v4 {
        background: white;
        border-radius: var(--radius-lg);
        max-width: 900px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        direction: rtl;
    }

    .modal-header-v4 {
        padding: 18px 22px;
        border-bottom: 1px solid var(--gray-200);
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: sticky;
        top: 0;
        background: white;
        z-index: 1;
    }

    .modal-title-v4 {
        font-size: 1.2rem;
        font-weight: 700;
    }

    .modal-close-v4 {
        background: var(--gray-100);
        border: none;
        font-size: 1.2rem;
        cursor: pointer;
        color: var(--gray-500);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .modal-close-v4:hover {
        background: var(--gray-200);
    }

    .modal-body-v4 {
        padding: 22px;
    }

    /* ============================================================ */
    /* 📱 Responsive Design */
    /* ============================================================ */

    /* Tablets */
    @media (max-width: 1024px) {
        .summary-grid-v4 { 
            grid-template-columns: repeat(4, 1fr); 
            gap: 12px;
        }
        .campaigns-grid-v4 { 
            grid-template-columns: repeat(2, 1fr); 
        }
        .campaign-stats-v4 { 
            grid-template-columns: repeat(3, 1fr); 
        }
    }

    /* Mobile Large */
    @media (max-width: 768px) {
        .dashboard-v4 { 
            padding: 12px; 
        }
        .dashboard-title-v4 { 
            font-size: 1.8rem; 
        }
        .dashboard-subtitle-v4 { 
            font-size: 1rem; 
        }
        .summary-grid-v4 { 
            grid-template-columns: repeat(2, 1fr); 
            gap: 10px;
        }
        .summary-card-v4 {
            padding: 14px;
        }
        .summary-value-v4 {
            font-size: 1.5rem;
        }
        .summary-label-v4 {
            font-size: 0.85rem;
        }
        .summary-icon-v4 {
            font-size: 1.8rem;
        }
        .campaigns-grid-v4 { 
            grid-template-columns: 1fr; 
        }
        .filter-row-v4 { 
            flex-direction: column; 
        }
        .filter-group-v4 { 
            width: 100%; 
            min-width: auto;
        }
        .campaigns-header-v4 {
            flex-direction: column;
            align-items: stretch;
        }
        .campaigns-controls-v4 { 
            width: 100%; 
            flex-direction: column; 
        }
        .search-input-v4 { 
            width: 100%; 
            min-width: auto; 
        }
        .charts-grid-v4 { 
            grid-template-columns: 1fr; 
        }
        .campaign-stats-v4 {
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
        }
        .stat-item-v4 {
            padding: 10px 6px;
        }
        .stat-value-v4 {
            font-size: 1rem;
        }
        .stat-label-v4 {
            font-size: 0.7rem;
        }
        .campaign-header-v4 {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
        }
        .campaign-badge-v4 {
            align-self: flex-end;
        }
        .campaign-table-v4 {
            font-size: 0.7rem;
        }
        .campaign-table-v4 th,
        .campaign-table-v4 td {
            padding: 6px 3px;
        }
        .btn-row-v4 {
            flex-direction: column;
        }
        .btn-details-v4,
        .btn-tiktok-v4 {
            width: 100%;
            text-align: center;
        }
        .modal-content-v4 {
            margin: 10px;
            max-height: 95vh;
        }
        .modal-body-v4 {
            padding: 14px;
        }
    }

    /* Mobile Small */
    @media (max-width: 480px) {
        .dashboard-v4 { 
            padding: 8px; 
        }
        .dashboard-title-v4 { 
            font-size: 1.4rem; 
        }
        .dashboard-subtitle-v4 { 
            font-size: 0.9rem; 
        }
        .summary-grid-v4 { 
            grid-template-columns: repeat(2, 1fr); 
            gap: 8px;
        }
        .summary-card-v4 {
            padding: 10px;
        }
        .summary-value-v4 {
            font-size: 1.2rem;
        }
        .summary-label-v4 {
            font-size: 0.75rem;
        }
        .summary-icon-v4 {
            font-size: 1.5rem;
            margin-bottom: 6px;
        }
        .filter-card-v4 {
            padding: 14px;
        }
        .filter-select-v4, 
        .filter-input-v4 {
            padding: 10px 12px;
            font-size: 0.9rem;
        }
        .btn-primary-v4 {
            width: 100%;
            justify-content: center;
            padding: 12px;
        }
        .campaigns-title-v4 {
            font-size: 1.2rem;
        }
        .campaign-card-v4 {
            border-radius: 10px;
        }
        .campaign-header-v4 {
            padding: 12px;
        }
        .campaign-name-v4 {
            font-size: 0.95rem;
        }
        .campaign-product-v4 {
            font-size: 0.8rem;
        }
        .campaign-body-v4 {
            padding: 12px;
        }
        .campaign-stats-v4 {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 6px;
        }
        .stat-item-v4 {
            padding: 6px;
        }
        .stat-value-v4 {
            font-size: 1rem;
        }
        .stat-label-v4 {
            font-size: 0.65rem;
        }
        .campaign-table-v4 {
            font-size: 0.65rem;
        }
        .campaign-table-v4 th,
        .campaign-table-v4 td {
            padding: 5px 2px;
        }
        .alert-item-v4 {
            font-size: 0.8rem;
            padding: 8px;
        }
        .btn-details-v4,
        .btn-tiktok-v4 {
            padding: 10px;
            font-size: 0.9rem;
        }
        .simple-chart-v4 {
            padding: 14px;
        }
        .chart-title-v4 {
            font-size: 1rem;
        }
    }

    /* Very Small Screens */
    @media (max-width: 360px) {
        .summary-grid-v4 { 
            grid-template-columns: 1fr 1fr; 
            gap: 6px;
        }
        .summary-value-v4 {
            font-size: 1rem;
        }
        .summary-label-v4 {
            font-size: 0.7rem;
        }
        .campaign-stats-v4 {
            grid-template-columns: 1fr 1fr !important;
        }
        .stat-value-v4 {
            font-size: 0.9rem;
        }
    }
`;

// ============================================================
// 📊 Helper Functions
// ============================================================

const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    
    if (absNum >= 1000000000) {
        return sign + (absNum / 1000000000).toFixed(1) + 'B';
    }
    if (absNum >= 1000000) {
        return sign + (absNum / 1000000).toFixed(1) + 'M';
    }
    if (absNum >= 1000) {
        return sign + (absNum / 1000).toFixed(1) + 'K';
    }
    return sign + Math.round(absNum).toLocaleString('en-US');
};

const formatFullNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return Math.round(num).toLocaleString('en-US');
};

const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0 د.ع';
    return Math.round(num).toLocaleString('en-US') + ' د.ع';
};

// ============================================================
// 📊 Simple Bar Chart Component
// ============================================================

const SimpleBarChart = ({ data, title, dataKey, colorType = 'primary' }) => {
    if (!data || data.length === 0) return null;

    const values = data.map(d => d[dataKey] || 0);
    const maxValue = Math.max(...values.map(Math.abs), 1);
    const chartHeight = 160; // ارتفاع ثابت بالبكسل

    return (
        <div className="simple-chart-v4">
            <h3 className="chart-title-v4">{title}</h3>
            <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-around',
                height: `${chartHeight + 50}px`,
                padding: '30px 10px 10px 10px',
                background: '#f9fafb',
                borderRadius: '8px',
                gap: '8px',
                borderBottom: '2px solid #e5e7eb'
            }}>
                {data.map((item, index) => {
                    const value = item[dataKey] || 0;
                    const barHeight = Math.max((Math.abs(value) / maxValue) * chartHeight, 8);
                    const isNegative = value < 0;
                    
                    let bgColor = '#10b981'; // أخضر للربح
                    if (colorType === 'sales') bgColor = '#f59e0b'; // برتقالي للمبيعات
                    else if (isNegative) bgColor = '#ef4444'; // أحمر للخسارة
                    
                    return (
                        <div key={index} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            minWidth: '40px'
                        }}>
                            <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <div style={{
                                    width: '32px',
                                    height: `${barHeight}px`,
                                    background: `linear-gradient(180deg, ${bgColor}, ${bgColor}dd)`,
                                    borderRadius: '6px 6px 0 0',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                    position: 'relative',
                                    transition: 'all 0.3s'
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        top: '-22px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontSize: '0.65rem',
                                        fontWeight: '700',
                                        color: '#374151',
                                        whiteSpace: 'nowrap',
                                        background: 'white',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                        {formatNumber(value)}
                                    </span>
                                </div>
                            </div>
                            <span style={{
                                fontSize: '0.7rem',
                                color: '#6b7280',
                                marginTop: '8px',
                                fontWeight: '500'
                            }}>
                                {item.date}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================
// 🎯 Main Component
// ============================================================

const CampaignDashboardV4 = () => {
    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [summary, setSummary] = useState(null);
    const [filterType, setFilterType] = useState('2');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('updated');
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    // ✅ فلتر الصفحة
    const [pageId, setPageId] = useState('0');
    const [pages, setPages] = useState([]);

    useEffect(() => {
        if (!document.getElementById('dashboard-v4-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'dashboard-v4-styles';
            styleEl.textContent = styles;
            document.head.appendChild(styleEl);
        }
        // ✅ جلب قائمة الصفحات
        fetchPages();
    }, []);

    // ✅ جلب قائمة الصفحات
    const fetchPages = async () => {
        try {
            const response = await axios.get(
                `${host}users/pages/name/all`,
                { headers: { token: cookies.get("token") } }
            );
            setPages(response.data.data.pages || []);
        } catch (error) {
            console.error('Error fetching pages:', error);
        }
    };

    // ✅ استخدام API الجديد مع page_id
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const from = filterType === '0' ? moment(fromDate).format('DD/MM/YYYY') : '';
            const to = filterType === '0' ? moment(toDate).format('DD/MM/YYYY') : '';

            const response = await axios.get(
                `${host}dashbord/autoCampV4?check=${filterType}&from=${from}&to=${to}&page_id=${pageId}`,
                { headers: { token: cookies.get("token") } }
            );

            console.log('API Response:', response.data);
            
            // ✅ استخدام البيانات مباشرة من الـ API الجديد
            setCampaigns(response.data.campaigns || []);
            setSummary(response.data.summary || null);
            
            toast.success('تم تحميل البيانات بنجاح ✅');
        } catch (error) {
            console.error(error);
            toast.error('حدث خطأ في تحميل البيانات ❌');
        } finally {
            setLoading(false);
        }
    }, [filterType, fromDate, toDate, pageId]);

    // Filtered & Sorted Campaigns
    const filteredCampaigns = useMemo(() => {
        let result = [...campaigns];

        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            result = result.filter(c => 
                (c.name && c.name.toLowerCase().includes(search)) ||
                (c.item?.name && c.item.name.toLowerCase().includes(search))
            );
        }

        switch (sortBy) {
            case 'updated':
                result.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                break;
            case 'roi':
                result.sort((a, b) => (b.financials?.roi || 0) - (a.financials?.roi || 0));
                break;
            case 'profit':
                result.sort((a, b) => (b.financials?.net_profit || 0) - (a.financials?.net_profit || 0));
                break;
            case 'orders':
                result.sort((a, b) => (b.orders?.successful || 0) - (a.orders?.successful || 0));
                break;
            case 'cpa':
                result.sort((a, b) => (a.financials?.cpa || 0) - (b.financials?.cpa || 0));
                break;
            case 'inventory':
                result.sort((a, b) => (a.item?.count || 0) - (b.item?.count || 0));
                break;
            case 'profitable':
                result = result.filter(c => (c.financials?.net_profit || 0) > 0);
                break;
            case 'losing':
                result = result.filter(c => (c.financials?.net_profit || 0) <= 0);
                break;
        }

        return result;
    }, [campaigns, searchTerm, sortBy]);

    // ✅ Chart Data - صافي الربح اليومي
    const profitChartData = useMemo(() => {
        if (campaigns.length === 0) return [];

        const dailyMap = new Map();
        campaigns.forEach(campaign => {
            (campaign.daily_stats || []).forEach(day => {
                const existing = dailyMap.get(day.date) || { profit: 0, orders: 0, revenue: 0 };
                dailyMap.set(day.date, {
                    profit: existing.profit + (day.net_profit || 0),
                    orders: existing.orders + (day.orders?.successful || 0),
                    revenue: existing.revenue + (day.revenue || 0)
                });
            });
        });

        return Array.from(dailyMap.entries())
            .map(([date, data]) => ({ 
                date: date.substring(0, 5), // فقط DD/MM
                profit: data.profit, 
                orders: data.orders,
                revenue: data.revenue
            }))
            .sort((a, b) => {
                const [dayA, monthA] = a.date.split('/').map(Number);
                const [dayB, monthB] = b.date.split('/').map(Number);
                if (monthA !== monthB) return monthA - monthB;
                return dayA - dayB;
            })
            .slice(-10);
    }, [campaigns]);

    // ✅ Chart Data - المبيعات اليومية
    const salesChartData = useMemo(() => {
        if (campaigns.length === 0) return [];

        const dailyMap = new Map();
        campaigns.forEach(campaign => {
            (campaign.daily_stats || []).forEach(day => {
                const existing = dailyMap.get(day.date) || 0;
                // حساب المبيعات = عدد الطلبات الناجحة × متوسط سعر البيع (تقدير)
                const dayRevenue = day.revenue || ((day.orders?.successful || 0) * 50000);
                dailyMap.set(day.date, existing + dayRevenue);
            });
        });

        return Array.from(dailyMap.entries())
            .map(([date, revenue]) => ({ 
                date: date.substring(0, 5),
                revenue: revenue
            }))
            .sort((a, b) => {
                const [dayA, monthA] = a.date.split('/').map(Number);
                const [dayB, monthB] = b.date.split('/').map(Number);
                if (monthA !== monthB) return monthA - monthB;
                return dayA - dayB;
            })
            .slice(-10);
    }, [campaigns]);

    if (!cookies.get("token")) {
        return <Login />;
    }

    return (
        <div className="dashboard-v4">
            {loading && (
                <div className="loading-overlay-v4">
                    <div className="spinner-v4" />
                </div>
            )}

            <div className="dashboard-container">
                <div className="dashboard-header-v4">
                    <h1 className="dashboard-title-v4">📊 لوحة تحكم الحملات V4</h1>
                    <p className="dashboard-subtitle-v4">تحليل متقدم مع مؤشرات أداء دقيقة</p>
                </div>

                <div className="filter-card-v4">
                    <div className="filter-row-v4">
                        <div className="filter-group-v4">
                            <label className="filter-label-v4">📅 نوع العرض</label>
                            <select 
                                className="filter-select-v4"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="2">📈 هذا الشهر</option>
                                <option value="1">🟢 النشطة فقط</option>
                                <option value="0">📅 فترة محددة</option>
                            </select>
                        </div>

                        {/* ✅ فلتر الصفحة */}
                        <div className="filter-group-v4">
                            <label className="filter-label-v4">📱 الصفحة</label>
                            <select 
                                className="filter-select-v4"
                                value={pageId}
                                onChange={(e) => setPageId(e.target.value)}
                            >
                                <option value="0">كل الصفحات</option>
                                {pages.map(page => (
                                    <option key={page.id} value={page.id}>
                                        {page.name || page.page_name || `صفحة ${page.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {filterType === '0' && (
                            <>
                                <div className="filter-group-v4">
                                    <label className="filter-label-v4">من تاريخ</label>
                                    <input
                                        type="date"
                                        className="filter-input-v4"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>
                                <div className="filter-group-v4">
                                    <label className="filter-label-v4">إلى تاريخ</label>
                                    <input
                                        type="date"
                                        className="filter-input-v4"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        <button 
                            className="btn-primary-v4"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            {loading ? '⏳ جاري...' : '🔍 تحليل البيانات'}
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="summary-grid-v4">
                        <div className="summary-card-v4">
                            <div className="summary-icon-v4">📦</div>
                            <div className="summary-value-v4">{summary.total_successful_orders || 0}</div>
                            <div className="summary-label-v4">طلبات ناجحة</div>
                        </div>

                        {role !== 2 && (
                            <div className="summary-card-v4 success">
                                <div className="summary-icon-v4">💰</div>
                                <div className="summary-value-v4">{formatNumber(summary.total_net_profit)}</div>
                                <div className="summary-label-v4">صافي الربح</div>
                            </div>
                        )}

                        <div className="summary-card-v4">
                            <div className="summary-icon-v4">📈</div>
                            <div className="summary-value-v4">{summary.average_roi || 0}%</div>
                            <div className="summary-label-v4">متوسط ROI</div>
                        </div>

                        {role !== 2 && (
                            <div className="summary-card-v4 warning">
                                <div className="summary-icon-v4">💸</div>
                                <div className="summary-value-v4">{formatNumber(summary.average_cpa)}</div>
                                <div className="summary-label-v4">متوسط CPA</div>
                            </div>
                        )}

                        <div className="summary-card-v4 success">
                            <div className="summary-icon-v4">✅</div>
                            <div className="summary-value-v4">{summary.profitable_campaigns || 0}</div>
                            <div className="summary-label-v4">رابحة</div>
                        </div>

                        <div className="summary-card-v4 danger">
                            <div className="summary-icon-v4">❌</div>
                            <div className="summary-value-v4">{summary.losing_campaigns || 0}</div>
                            <div className="summary-label-v4">خاسرة</div>
                        </div>

                        {role !== 2 && (
                            <>
                                <div className="summary-card-v4 info">
                                    <div className="summary-icon-v4">💵</div>
                                    <div className="summary-value-v4">{formatNumber(summary.total_revenue)}</div>
                                    <div className="summary-label-v4">المبيعات</div>
                                </div>

                                <div className="summary-card-v4">
                                    <div className="summary-icon-v4">📢</div>
                                    <div className="summary-value-v4">{formatNumber(summary.total_ad_spend)}</div>
                                    <div className="summary-label-v4">الإعلانات</div>
                                </div>
                            </>
                        )}

                        {/* ✅ نسب الطلبات */}
                        <div className="summary-card-v4 success">
                            <div className="summary-icon-v4">📊</div>
                            <div className="summary-value-v4">{summary.success_rate || 0}%</div>
                            <div className="summary-label-v4">نسبة النجاح</div>
                        </div>

                        <div className="summary-card-v4 warning">
                            <div className="summary-icon-v4">🚫</div>
                            <div className="summary-value-v4">{summary.cancel_rate || 0}%</div>
                            <div className="summary-label-v4">نسبة الإلغاء</div>
                        </div>

                        <div className="summary-card-v4 danger">
                            <div className="summary-icon-v4">↩️</div>
                            <div className="summary-value-v4">{summary.rejected_rate || 0}%</div>
                            <div className="summary-label-v4">نسبة الراجع</div>
                        </div>
                    </div>
                )}

                {/* ✅ Charts - صافي الربح والمبيعات */}
                {profitChartData.length > 0 && role !== 2 && (
                    <div className="charts-grid-v4">
                        <SimpleBarChart 
                            data={profitChartData} 
                            title="📈 صافي الربح اليومي (آخر 10 أيام)" 
                            dataKey="profit"
                            colorType="profit"
                        />
                        <SimpleBarChart 
                            data={salesChartData} 
                            title="💵 المبيعات اليومية (آخر 10 أيام)" 
                            dataKey="revenue"
                            colorType="sales"
                        />
                    </div>
                )}

                {/* Campaigns Header */}
                <div className="campaigns-header-v4">
                    <h2 className="campaigns-title-v4">🎯 الحملات ({filteredCampaigns.length})</h2>
                    <div className="campaigns-controls-v4">
                        <input
                            type="text"
                            className="search-input-v4"
                            placeholder="🔍 بحث..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="filter-select-v4"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            style={{ minWidth: '150px' }}
                        >
                            <option value="updated">⏰ آخر تحديث</option>
                            <option value="roi">📈 الأعلى ROI</option>
                            <option value="profit">💰 الأعلى ربحاً</option>
                            <option value="orders">📦 الأكثر طلبات</option>
                            <option value="cpa">💸 الأقل CPA</option>
                            <option value="inventory">📦 أقل مخزون</option>
                            <option value="profitable">✅ رابحة</option>
                            <option value="losing">❌ خاسرة</option>
                        </select>
                    </div>
                </div>

                {/* Campaigns Grid */}
                <div className="campaigns-grid-v4">
                    {filteredCampaigns.map((campaign) => (
                        <div 
                            key={campaign.id} 
                            className={`campaign-card-v4 ${(campaign.financials?.net_profit || 0) > 0 ? 'profitable' : 'losing'}`}
                        >
                            <div className="campaign-header-v4">
                                <div>
                                    <div className="campaign-name-v4">🎯 {campaign.name}</div>
                                    <div className="campaign-product-v4">{campaign.item?.name}</div>
                                </div>
                                <span className={`campaign-badge-v4 ${(campaign.financials?.net_profit || 0) > 0 ? 'profit' : 'loss'}`}>
                                    ROI: {campaign.financials?.roi || 0}%
                                </span>
                            </div>

                            <div className="campaign-body-v4">
                                {/* ✅ إضافة الميزانية اليومية */}
                                <div className={`campaign-stats-v4 ${role !== 2 ? 'five-cols' : ''}`}>
                                    {role !== 2 && (
                                        <div className="stat-item-v4">
                                            <div className="stat-value-v4">{formatNumber(campaign.financials?.net_profit)}</div>
                                            <div className="stat-label-v4">💰 صافي</div>
                                        </div>
                                    )}
                                    <div className="stat-item-v4">
                                        <div className="stat-value-v4">{campaign.orders?.successful || 0}</div>
                                        <div className="stat-label-v4">📦 ناجحة</div>
                                    </div>
                                    {role !== 2 && (
                                        <div className="stat-item-v4">
                                            <div className="stat-value-v4">{formatNumber(campaign.financials?.cpa)}</div>
                                            <div className="stat-label-v4">💸 CPA</div>
                                        </div>
                                    )}
                                    <div className="stat-item-v4">
                                        <div className="stat-value-v4">{campaign.item?.count || 0}</div>
                                        <div className="stat-label-v4">📦 مخزون</div>
                                    </div>
                                    {/* ✅ الميزانية اليومية */}
                                    {role !== 2 && (
                                        <div className="stat-item-v4 highlight">
                                            <div className="stat-value-v4">{formatNumber(campaign.budget_daily)}</div>
                                            <div className="stat-label-v4">💵 ميزانية/يوم</div>
                                        </div>
                                    )}
                                </div>

                                {/* Mini Stats Table */}
                                <table className="campaign-table-v4">
                                    <thead>
                                        <tr>
                                            <th>📅</th>
                                            <th>✅</th>
                                            <th>⏳</th>
                                            <th>🚚</th>
                                            <th>❌</th>
                                            {role !== 2 && <th>💰</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(campaign.last_3_days || []).slice(0, 3).map((day, i) => (
                                            <tr key={i} className={!day.is_profitable ? 'red-row' : ''}>
                                                <td>{day.date}</td>
                                                <td>{day.orders?.successful || 0}</td>
                                                <td>{day.orders?.pending || 0}</td>
                                                <td>{day.orders?.in_delivery || 0}</td>
                                                <td>{day.orders?.cancelled || 0}</td>
                                                {role !== 2 && <td>{formatNumber(day.net_profit)}</td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Alerts */}
                                {campaign.alerts && campaign.alerts.length > 0 && (
                                    <div className="campaign-alerts-v4">
                                        {campaign.alerts.slice(0, 2).map((alert, i) => (
                                            <div key={i} className={`alert-item-v4 ${alert.type}`}>
                                                {alert.message}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="btn-row-v4">
                                    <button 
                                        className="btn-details-v4"
                                        onClick={() => setSelectedCampaign(campaign)}
                                    >
                                        📋 التفاصيل
                                    </button>
                                    <button
                                        className="btn-tiktok-v4"
                                        onClick={() => window.open(
                                            `https://ads.tiktok.com/i18n/perf/campaign?aadvid=${campaign.aid}&keyword=${campaign.comp_id}`,
                                            '_blank'
                                        )}
                                    >
                                        TikTok
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Details Modal */}
                {selectedCampaign && (
                    <div className="modal-overlay-v4" onClick={() => setSelectedCampaign(null)}>
                        <div className="modal-content-v4" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-v4">
                                <h2 className="modal-title-v4">📊 {selectedCampaign.name}</h2>
                                <button className="modal-close-v4" onClick={() => setSelectedCampaign(null)}>✕</button>
                            </div>
                            <div className="modal-body-v4">
                                {role !== 2 && (
                                    <div className="summary-grid-v4" style={{ marginBottom: '20px' }}>
                                        <div className="summary-card-v4">
                                            <div className="summary-value-v4">{formatCurrency(selectedCampaign.financials?.total_revenue)}</div>
                                            <div className="summary-label-v4">المبيعات</div>
                                        </div>
                                        <div className="summary-card-v4">
                                            <div className="summary-value-v4">{formatCurrency(selectedCampaign.financials?.ad_spend)}</div>
                                            <div className="summary-label-v4">الإعلانات</div>
                                        </div>
                                        <div className="summary-card-v4 success">
                                            <div className="summary-value-v4">{formatCurrency(selectedCampaign.financials?.net_profit)}</div>
                                            <div className="summary-label-v4">الصافي</div>
                                        </div>
                                        <div className="summary-card-v4 info">
                                            <div className="summary-value-v4">{formatCurrency(selectedCampaign.budget_daily)}</div>
                                            <div className="summary-label-v4">ميزانية/يوم</div>
                                        </div>
                                    </div>
                                )}

                                <h3 style={{ marginBottom: '12px' }}>📅 التفاصيل اليومية ({selectedCampaign.days_active} يوم)</h3>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="campaign-table-v4">
                                        <thead>
                                            <tr>
                                                <th>التاريخ</th>
                                                <th>ناجحة</th>
                                                <th>انتظار</th>
                                                <th>توصيل</th>
                                                <th>ملغي</th>
                                                <th>راجع</th>
                                                {role !== 2 && <th>الصافي</th>}
                                                {role !== 2 && <th>CPA</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(selectedCampaign.daily_stats || []).map((day, i) => (
                                                <tr key={i} className={!day.is_profitable ? 'red-row' : ''}>
                                                    <td>{day.date}</td>
                                                    <td>{day.orders?.successful || 0}</td>
                                                    <td>{day.orders?.pending || 0}</td>
                                                    <td>{day.orders?.in_delivery || 0}</td>
                                                    <td>{day.orders?.cancelled || 0}</td>
                                                    <td>{day.orders?.rejected || 0}</td>
                                                    {role !== 2 && <td>{formatNumber(day.net_profit)}</td>}
                                                    {role !== 2 && <td>{formatNumber(day.cpa)}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ToastContainer position="bottom-left" rtl />
        </div>
    );
};

export default CampaignDashboardV4;